import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import { Factory, Plus, Clock, ChevronLeft, ChevronRight, Hash } from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";

export default function ManufacturerPage() {
  const [formData, setFormData] = useState({
    company: "",
  });
  const [loading, setLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchManufacturers = async () => {
    setListLoading(true);
    try {
      const token = localStorage.getItem("dbToken");
      const params = {
        pageNo,
        pageSize,
      };

      const response = await axios.get(`${server}/manufacturerMaster/getAll`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setManufacturers(response.data.data);
        setTotalCount(response.data.count);
      } else {
        toast.error("Failed to fetch manufacturers");
      }
    } catch (error) {
      console.error("Error fetching manufacturers:", error);
      toast.error(error.response?.data?.message || "Error fetching manufacturers");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchManufacturers();
  }, [pageNo, pageSize]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("dbToken");
      await axios.post(`${server}/manufacturerMaster/new`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Manufacturer added successfully!");
      setFormData({
        company: "",
      });
      fetchManufacturers(); // Refresh the list
    } catch (error) {
      console.error("Error adding manufacturer:", error);
      toast.error(error.response?.data?.message || "Failed to add manufacturer");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalCount / pageSize)) {
      setPageNo(newPage);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
       {/* Header Section */}
       <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Manufacturers</h1>
              <p className="text-sm text-gray-500 mt-1">Manage device manufacturers and brands.</p>
          </div>
      </div>

       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           {/* Add Manufacturer Card */}
           <div className="lg:col-span-1">
               <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 sticky top-6">
                   <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                        <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center">
                            <Factory className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Add New</h2>
                            <p className="text-xs text-gray-500">Register a new manufacturer</p>
                        </div>
                   </div>

                   <form onSubmit={handleSubmit} className="space-y-4">
                     <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Company Name</label>
                        <div className="relative group">
                            <Factory className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              placeholder="e.g. Cisco"
                              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                              required
                            />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg font-medium text-white shadow-sm transition-all mt-4 ${
                            loading
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                            : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
                        }`}
                        >
                        {loading ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                                <span>Adding...</span>
                            </>
                        ) : (
                            <>
                                <Plus className="h-4 w-4" />
                                <span>Add Manufacturer</span>
                            </>
                        )}
                      </button>
                   </form>
               </div>
           </div>

           {/* List Section */}
           <div className="lg:col-span-2 space-y-4">
               <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
                   <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    <th className="px-6 py-4 w-20">ID</th>
                                    <th className="px-6 py-4">Company Name</th>
                                    <th className="px-6 py-4 text-right">Created At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {listLoading ? (
                                    // Loading skeleton for rows
                                    [1, 2, 3, 4, 5].map((i) => (
                                        <tr key={i}>
                                            <td className="px-6 py-4"><Skeleton className="h-4 w-8" /></td>
                                            <td className="px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                                            <td className="px-6 py-4"><div className="flex justify-end"><Skeleton className="h-4 w-24" /></div></td>
                                        </tr>
                                    ))
                                ) : manufacturers.length === 0 ? (
                                    <tr>
                                       <td colSpan="3" className="px-6 py-12 text-center text-gray-500">
                                          <div className="flex flex-col items-center justify-center gap-2">
                                              <div className="p-3 bg-gray-50 rounded-full">
                                                  <Factory className="h-6 w-6 text-gray-300" />
                                              </div>
                                              <span className="text-sm font-medium">No manufacturers found</span>
                                          </div>
                                       </td>
                                    </tr>
                                ) : (
                                    manufacturers.map((item) => (
                                        <tr key={item.id} className="group hover:bg-blue-50/30 transition-colors">
                                            <td className="px-6 py-3.5 text-gray-500 font-mono text-xs">
                                                #{item.id}
                                            </td>
                                            <td className="px-6 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 font-bold text-xs border border-indigo-100">
                                                        {item.company.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="font-semibold text-gray-900 text-sm">
                                                        {item.company}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3.5 text-right">
                                               <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
                                                   <Clock className="h-3.5 w-3.5 text-gray-400" />
                                                   {new Date(item.createdAt).toLocaleDateString()}
                                               </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                   </div>

                    {/* Pagination Footer */}
                    {totalCount > 0 && (
                        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-xs font-medium text-gray-500">
                            Showing <span className="text-gray-900 font-bold">{manufacturers.length}</span> of <span className="text-gray-900 font-bold">{totalCount}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button
                            onClick={() => handlePageChange(pageNo - 1)}
                            disabled={pageNo === 1}
                            className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                            <ChevronLeft className="h-4 w-4" />
                            </button>
                            <div className="px-4 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-mono font-medium text-gray-700 shadow-sm">
                            Page {pageNo} of {totalPages || 1}
                            </div>
                            <button
                            onClick={() => handlePageChange(pageNo + 1)}
                            disabled={pageNo === totalPages || totalPages === 0}
                            className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                            <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                        </div>
                    )}
               </div>
           </div>
       </div>
    </div>
  );
}
